// Translates mouse & keyboard intent into game actions, D2-style: one button does what you mean.

import { SKILLS } from '../../shared/classes';
import { ME_DOWNED, ME_STUNNED } from '../../shared/protocol';
import { nearestWalkable } from '../../shared/map';
import { audio } from '../audio';
import { input } from '../input';
import { ui } from '../ui';
import { Hud } from './hud';
import { WorldRenderer } from './render';
import { CObject, CUnit, GameState } from './state';

const SKILL_KEYS: [string, number][] = [
  ['Digit1', 2],
  ['Digit2', 3],
  ['Digit3', 4],
  ['Digit4', 5],
];

/** Distance from an object at which we stop walking and interact. */
function reachFor(o: CObject): number {
  switch (o.type) {
    case 'gate':
      return 4.5;
    case 'npc':
    case 'stash':
      return 3;
    case 'portal':
      return o.sub === 'arena' ? 3 : 1.6;
    case 'stairsDown':
    case 'stairsUp':
      return 1.8;
    default:
      return 1.8;
  }
}

export class Controls {
  /** Object or item we're walking to in order to use it. */
  private pendingAct: { kind: 'obj'; o: CObject } | { kind: 'item'; id: number; x: number; y: number } | { kind: 'revive'; id: number } | null = null;
  private attackId: number | null = null;
  /** Skill to use on arrival at the attack target. */
  private attackSkill: string | null = null;
  private holdWalk = false;

  constructor(private st: GameState, private world: WorldRenderer, private hud: Hud) {}

  update(): void {
    const st = this.st;
    const hud = this.hud;
    if (!st.map || !st.hero) return;

    input.textMode = hud.chatOpen;
    st.inputLocked = hud.chatOpen;

    if (hud.chatOpen) {
      hud.updateChat();
      return;
    }

    if (input.pressed.has('Enter') || input.pressed.has('NumpadEnter')) {
      hud.chatOpen = true;
      input.textMode = true;
      return;
    }
    if (input.pressed.has('Escape')) {
      if (hud.anyPanel) hud.closeAll();
      else hud.center = 'menu';
    }
    if (input.pressed.has('KeyC')) hud.togglePanel('char');
    if (input.pressed.has('KeyI')) hud.togglePanel('inv');
    if (input.pressed.has('KeyK')) hud.togglePanel('skills');
    if (input.pressed.has('KeyP')) hud.togglePanel('voice');
    if (input.pressed.has('F1')) hud.togglePanel('help');
    if (input.pressed.has('Tab')) hud.automap = !hud.automap;
    if (input.pressed.has('KeyM')) audio.toggleMute();
    if (input.pressed.has('KeyN')) audio.toggleMusic();
    if (input.pressed.has('KeyT')) hud.recall();
    if (input.pressed.has('KeyQ')) st.send({ t: 'pot', k: 'hp' });
    if (input.pressed.has('KeyE')) st.send({ t: 'pot', k: 'mp' });

    const dead = (st.me[9] & ME_DOWNED) !== 0;
    if (dead) {
      st.stopMoving();
      this.pendingAct = null;
      this.attackId = null;
      return;
    }

    const shift = input.down.has('ShiftLeft') || input.down.has('ShiftRight');
    const [wx, wy] = this.world.screenToWorld(input.mx, input.my);
    const overUi = ui.captured;
    const hoverUnit = overUi ? null : this.world.hoverUnit;
    const hoverItem = overUi ? null : this.world.hoverItem;
    const hoverObject = overUi ? null : this.world.hoverObject;

    if (input.lmbPressed && !overUi) {
      this.holdWalk = false;
      this.pendingAct = null;
      this.attackId = null;
      if (shift) {
        // Force-stand: handled in the hold logic below.
      } else if (hoverUnit && this.isHostile(hoverUnit)) {
        this.beginAttack(hoverUnit);
      } else if (hoverUnit && hoverUnit.kind === 'player' && hoverUnit.hp <= 0) {
        this.pendingAct = { kind: 'revive', id: hoverUnit.id };
        st.goal = { kind: 'act', id: hoverUnit.id, x: hoverUnit.x, y: hoverUnit.y, range: 1.8 };
      } else if (hoverObject) {
        this.pendingAct = { kind: 'obj', o: hoverObject };
        st.goal = { kind: 'act', id: hoverObject.id, x: hoverObject.x, y: hoverObject.y, range: reachFor(hoverObject) };
      } else if (hoverItem) {
        this.pendingAct = { kind: 'item', id: hoverItem.id, x: hoverItem.x, y: hoverItem.y };
        st.goal = { kind: 'act', id: hoverItem.id, x: hoverItem.x, y: hoverItem.y, range: 1.2 };
      } else {
        this.holdWalk = true;
        this.walkTo(wx, wy);
      }
    }

    if (!input.lmb) {
      this.holdWalk = false;
    }

    // Holding the button: keep walking toward the cursor, or keep swinging.
    if (input.lmb && this.holdWalk && !overUi && !shift) {
      this.walkTo(wx, wy);
    }
    if (input.lmb && !this.holdWalk && !this.attackId && !this.pendingAct && hoverUnit && !shift && !overUi && this.isHostile(hoverUnit)) {
      this.beginAttack(hoverUnit);
    }

    if (input.lmb && shift && !overUi) {
      st.stopMoving();
      this.pendingAct = null;
      this.attackId = null;
      const k = st.hero.binds[0];
      if (k) st.tryCast(k, wx, wy, hoverUnit?.id);
    }

    this.updateAttack();
    this.updatePending();

    // Right click & number keys: always at the cursor.
    if (!overUi && input.rmb) {
      const k = st.hero.binds[1];
      if (k) this.useSkill(k, wx, wy, hoverUnit);
    }
    for (const [code, idx] of SKILL_KEYS) {
      if (input.down.has(code)) {
        const k = st.hero.binds[idx];
        if (k) this.useSkill(k, wx, wy, hoverUnit, input.pressed.has(code));
      }
    }
  }

  private walkTo(wx: number, wy: number): void {
    const st = this.st;
    const map = st.map!;
    const p = nearestWalkable(map, wx, wy, 4);
    st.setMoveGoal(p.x, p.y);
  }

  private isHostile(u: CUnit): boolean {
    if (u.hp <= 0) return false;
    const myTeam = this.st.units.get(this.st.myUid)?.team ?? 1;
    return u.team !== myTeam;
  }

  private beginAttack(u: CUnit): void {
    const st = this.st;
    const k = st.hero!.binds[0];
    if (!k || st.zone?.safe) return;
    const def = SKILLS[k];
    if (!def) return;
    this.attackId = u.id;
    this.attackSkill = k;
    if (def.reach) {
      // Melee: walk into range, then swing.
      st.goal = { kind: 'unit', id: u.id, skill: k };
    } else {
      st.stopMoving();
    }
  }

  /** Runs the attack loop for the left-click target. */
  private updateAttack(): void {
    const st = this.st;
    if (this.attackId === null) return;
    const t = st.units.get(this.attackId);
    const k = this.attackSkill;
    if (!t || t.hp <= 0 || !k) {
      this.attackId = null;
      if (st.goal?.kind === 'unit') st.stopMoving();
      return;
    }
    const def = SKILLS[k];
    const held = input.lmb || input.rmb;
    if (def.reach) {
      const d = Math.hypot(t.x - st.px, t.y - st.py);
      if (d <= def.reach) {
        st.tryCast(k, t.x, t.y, t.id);
        if (!held) {
          this.attackId = null;
          st.stopMoving();
        }
      }
      return;
    }
    if (st.tryCast(k, t.x, t.y, t.id) && !held) {
      this.attackId = null;
    } else if (!held && st.castBlock(k) !== 'lock' && st.castBlock(k) !== 'cd') {
      this.attackId = null;
    }
  }

  private updatePending(): void {
    const st = this.st;
    const p = this.pendingAct;
    if (!p) return;
    if (p.kind === 'obj') {
      const d = Math.hypot(p.o.x - st.px, p.o.y - st.py);
      if (d <= reachFor(p.o) + 0.1) {
        st.stopMoving();
        this.pendingAct = null;
        if (p.o.type === 'npc' || p.o.type === 'stash' || p.o.type === 'gate' || (p.o.type === 'portal' && p.o.sub === 'arena')) {
          audio.play('click');
          this.hud.openFor(p.o.type, p.o.sub);
        } else {
          st.send({ t: 'act', id: p.o.id });
        }
      } else if (!st.goal) {
        this.pendingAct = null;
      }
    } else if (p.kind === 'item') {
      const d = Math.hypot(p.x - st.px, p.y - st.py);
      if (d <= 1.3) {
        st.stopMoving();
        this.pendingAct = null;
        st.send({ t: 'act', id: p.id });
      } else if (!st.goal) {
        this.pendingAct = null;
      }
    } else {
      const u = st.units.get(p.id);
      if (!u || u.hp > 0) {
        this.pendingAct = null;
        return;
      }
      const d = Math.hypot(u.x - st.px, u.y - st.py);
      if (d <= 2) {
        st.stopMoving();
        this.pendingAct = null;
        st.send({ t: 'act', id: p.id });
      }
    }
  }

  private useSkill(k: string, wx: number, wy: number, hover: CUnit | null, firstPress = true): void {
    const st = this.st;
    if ((st.me[9] & ME_STUNNED) !== 0) return;
    const def = SKILLS[k];
    if (!def) return;
    if (st.zone?.safe) {
      if (firstPress && input.rmbPressed) audio.play('deny');
      return;
    }
    const block = st.castBlock(k);
    if (block === 'mana') st.complain('Not enough mana', '#3cbcfc');
    if (block) return;
    let id: number | undefined;
    if (hover && def.target !== 'self') id = hover.id;
    // Touch range skills walk into range when aimed at someone out of reach.
    if (def.reach && hover && this.isHostile(hover) && Math.hypot(hover.x - st.px, hover.y - st.py) > def.reach) {
      this.pendingAct = null;
      this.attackId = hover.id;
      this.attackSkill = k;
      st.goal = { kind: 'unit', id: hover.id, skill: k };
      return;
    }
    if (st.tryCast(k, wx, wy, id)) {
      // Casting interrupts walking but leaves a held attack alone.
      if (st.goal?.kind === 'point') st.stopMoving();
    }
  }
}
